import { Directive } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { SubscriptionUtils } from '../../../utils/subscription.utils';


/**
 * Clase base de los componentes de la aplicacion que proporciona metodos de utilidad
 * para la gestion de suscripciones
 */
@Directive()
export abstract class BaseDirectiveObject implements OnDestroy {

  protected subscriptionUtils = new SubscriptionUtils();

  /**
   * dar de baja suscripciones al destruir el componente
   */
  ngOnDestroy(): void {
    this.subscriptionUtils.unregisterAllSubscriptions();
  }

  /**
   * Registrar una suscripcion
   */
  protected registerSubscription(subscription: Subscription) {
    this.subscriptionUtils.registerSubscription(subscription);
  }

  /**
   * Dar de baja una suscripcion
   */
  protected unregisterSubscription(subscription: Subscription) {
    this.subscriptionUtils.unregisterSubscription(subscription);
  }

  /**
   * Suscribirse a un observable y registrarlo.
   *
   * Ejemplo de invocación:
   *  El siguiente código:
   *   let subscription = this.observable.subscribe((resultado: any) => console.log(resultado),
   *                             (err:any) => console.error(err),
   *                             () => console.log('fin'));
   *   this.registerSubscription(subscription);
   *
   * se transforma en:
   *   this.subscribe(observable, (resultado: any) => console.log(resultado),
   *                             (err:any) => console.error(err),
   *                             () => console.log('fin'));
   */
  protected subscribe<T>(observable: Observable<T>,
    next?: (value: T) => void,
    error?: (error: any) => void,
    complete?: () => void
  ): Subscription {
    let subscription = observable.subscribe((next) ? (value: T) => next(value) : undefined,
      (error) ? (err: any) => error(err) : undefined,
      (complete) ? () => complete() : undefined);
    this.registerSubscription(subscription);
    return subscription;
  }

  /**
   * Suscribirse a un observable y registrarlo. Igual que el método anterior pero permite
   * especificar uno, o dos metodos, entre 'next', 'error' o 'complete' sin tener que implementar 'next'
   *
   * Ejemplo de invocación:
   *  El siguiente código:
   *   this.observable.subscribe((resultado: any) => undefined,
   *                             (err:any) => undefined,
   *                             () => console.log('fin'));
   *
   *    equivalente a:
   *
   *   this.observable.subscribe({
   *                              complete: () => console.log('fin')
   *                            });
   *
   * se transforma en:
   *   this.subscribeObserver(observable, {
   *                              complete: () => console.log('fin')
   *                             });
   */
  protected subscribeObserver<T>(observable: Observable<T>,
    observer?: {
      next?: (value: T) => void,
      error?: (error: any) => void,
      complete?: () => void
    }): Subscription {

    return this.subscribe(observable,
      (observer) ? observer.next : undefined,
      (observer) ? observer.error : undefined,
      (observer) ? observer.complete : undefined
    )
  }

}